import { useEffect, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { documentsApi } from '../../api/documents'
import { RefusedError } from '../../api/client'
import { useJobLive } from '../../hooks/useJobLive'
import { useSession } from '../../hooks/useSession'
import { formatDate } from '../../lib/format'
import type { DocumentQuery, DocumentSummary } from '../../types/documents'
import { LineIcon } from '../LineIcon'
import { documentsKey, factsWord, formatBytes, kindGlyph, kindWords, partsWord, pendingJob, statusWord } from './documents'
import './documents.css'

// The documents on a person, an engagement or a company (REQ-119): each a row with its kind, its name (the file
// itself, as a download), what was counted in it, whether it has been read, how many facts it gave, who put it
// there and when. "Read again" starts a new reading; "Withdraw" is the uploader's alone. A document still being
// read is watched live, and when its job lands the list — and whatever the caller names — is refreshed.

const why = (e: unknown) => (e instanceof RefusedError ? e.refusal.message : e instanceof Error ? e.message : String(e))
const terminal = (s: string) => s === 'Completed' || s === 'Failed' || s === 'Cancelled'

/** Watches the job reading the newest pending document (or one the caller names); calls back once when it lands. */
export function useDocumentJobWatch(spoke: string, documents: DocumentSummary[] | null | undefined, jobId: string | null | undefined, onLanded: () => void) {
  const watching = jobId ?? pendingJob(documents)
  const { state: live } = useJobLive(spoke, watching)
  const handled = useRef<string | null>(null)
  const landed = !!live && terminal(live.job.status)
  const liveId = live?.job.id
  useEffect(() => {
    if (!landed || !liveId || handled.current === liveId) return
    handled.current = liveId
    onLanded()
  }, [landed, liveId, onLanded])
  return { watching, live }
}

export function DocumentList({ spoke, targets, invalidate = [], jobId }: {
  spoke: string
  /** which list: one of person, engagement, company */
  targets: DocumentQuery
  /** the query keys to refresh when a reading lands (the card, the lead, the workbench) */
  invalidate?: (string | null)[][]
  /** a fresh upload's job, when the caller has it; else the list watches its newest pending document */
  jobId?: string | null
}) {
  const qc = useQueryClient()
  const key = documentsKey(spoke, targets)
  const q = useQuery({ queryKey: key, queryFn: () => documentsApi.list(spoke, targets), staleTime: 5_000 })
  const [rereadJob, setRereadJob] = useState<string | null>(null)
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ['documents', spoke] })
    for (const k of invalidate) void qc.invalidateQueries({ queryKey: k })
  }
  // the callback is re-made each render; the watch only fires once per landed job, so that is fine
  useDocumentJobWatch(spoke, q.data, rereadJob ?? jobId, () => { setRereadJob(null); refresh() })

  if (q.isPending) return <p className="muted small">Reading the documents on file…</p>
  if (q.isError) return <p className="error small">{why(q.error)}</p>
  const docs = q.data ?? []
  if (docs.length === 0) return <p className="muted small document-none">No document on file here yet.</p>
  return (
    <ul className="plain document-list" aria-label="Documents">
      {docs.map((d) => <DocumentRow key={d.documentId} spoke={spoke} d={d} onReread={(id) => setRereadJob(id)} onChanged={refresh} />)}
    </ul>
  )
}

/** A short list of documents the caller already holds (the workbench focus carries its own): name, status, facts. */
export function DocumentMiniList({ spoke, documents, onLanded }: { spoke: string; documents: DocumentSummary[]; onLanded: () => void }) {
  useDocumentJobWatch(spoke, documents, null, onLanded)
  if (documents.length === 0) return null
  return (
    <ul className="plain document-list compact" aria-label="Documents on this opportunity">
      {documents.map((d) => (
        <li key={d.documentId} className={`document-row status-${d.textStatus}`}>
          <Glyph d={d} />
          <a className="document-name" href={documentsApi.fileUrl(spoke, d.documentId)} download={d.fileName} title={`${d.fileName} · ${formatBytes(d.bytes)}`}>{d.fileName}</a>
          <span className="muted small document-meta">
            <span className={`document-status status-${d.textStatus}`} title={d.textNote ?? undefined}>{statusWord(d.textStatus)}</span>
            {' · '}{factsWord(d.facts, d.textStatus)}
          </span>
        </li>
      ))}
    </ul>
  )
}

function Glyph({ d }: { d: DocumentSummary }) {
  const g = kindGlyph(d.kind)
  return 'icon' in g
    ? <LineIcon name={g.icon} size={15} className="document-glyph" title={kindWords[d.kind]} />
    : <span className="tag document-kind" title={kindWords[d.kind]}>{g.word}</span>
}

function DocumentRow({ spoke, d, onReread, onChanged }: { spoke: string; d: DocumentSummary; onReread: (jobId: string) => void; onChanged: () => void }) {
  const { user } = useSession()
  const mine = !!user && d.ownerId === user.id
  const reread = useMutation({ mutationFn: () => documentsApi.reread(spoke, d.documentId), onSuccess: (r) => { onReread(r.jobId); onChanged() } })
  const withdraw = useMutation({ mutationFn: () => documentsApi.withdraw(spoke, d.documentId), onSuccess: onChanged })
  const busy = reread.isPending || withdraw.isPending
  const failed = reread.error ?? withdraw.error
  const parts = partsWord(d.kind, d.parts)
  const reading = d.textStatus === 'pending'
  return (
    <li className={`document-row status-${d.textStatus}`} data-document={d.documentId}>
      <Glyph d={d} />
      <div className="document-main">
        <div className="document-line">
          <a className="document-name" href={documentsApi.fileUrl(spoke, d.documentId)} download={d.fileName} title={`Download ${d.fileName} · ${formatBytes(d.bytes)}`}>{d.fileName}</a>
          {parts && <span className="muted small">{parts}</span>}
          <span className={`document-status status-${d.textStatus}`} title={d.textNote ?? undefined}>{statusWord(d.textStatus)}</span>
          <span className="muted small">{factsWord(d.facts, d.textStatus)}</span>
        </div>
        {d.excerpt && !reading && <p className="muted small document-excerpt" title={d.excerpt}>{d.excerpt}</p>}
        <p className="muted small document-who">
          {mine ? 'uploaded by you' : 'uploaded by another rep'} · {formatDate(d.uploadedAt)}{d.readAt ? ` · read ${formatDate(d.readAt)}` : ''} · {formatBytes(d.bytes)}{d.textNote && !reading && d.textStatus !== 'extracted' ? ` · ${d.textNote}` : ''}
        </p>
        {failed && <p className="error small">{why(failed)}</p>}
      </div>
      <div className="document-actions">
        <button type="button" className="button small" onClick={() => reread.mutate()} disabled={busy || reading} title="Read the file again and draw its facts afresh.">{reread.isPending ? 'Starting…' : 'Read again'}</button>
        {mine && <button type="button" className="button small" onClick={() => withdraw.mutate()} disabled={busy} title="Take the document back; the facts drawn from it go with it.">{withdraw.isPending ? 'Withdrawing…' : 'Withdraw'}</button>}
      </div>
    </li>
  )
}
